import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { options as toOptions } from '../vocab'
import { DatePicker, DateTimePicker } from '@/components/erp/date-picker'

/**
 * The wizard's form primitives.
 *
 * One implementation of a labelled field, so the seven steps agree about where the label
 * sits, where the hint sits, and — the part that matters — where the **server's** field
 * error appears. A step that renders its own inputs renders its own idea of an error
 * message too, and the reporter learns a different layout on every screen.
 */

export function Field({
  label, hint, error, htmlFor, children, className,
}: {
  label: string
  hint?: ReactNode
  /** The server's message for this field, from a 422. Never a locally invented one. */
  error?: string
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={htmlFor} className="block text-erp-xs font-medium text-muted-foreground mb-1">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-erp-xs text-muted-foreground/80">{hint}</p>}
      {error && <p className="mt-1 text-erp-xs text-red-700 dark:text-red-400">{error}</p>}
    </div>
  )
}

/** Two columns above 640px, one below. The wizard's only layout decision. */
export function FieldGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
      {children}
    </div>
  )
}

export function TextInput({
  value, onChange, onBlur, placeholder, id, type = 'text', list, max,
}: {
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  placeholder?: string
  id?: string
  type?: 'text' | 'url' | 'number' | 'datetime-local' | 'date'
  list?: string
  max?: number
}) {
  // A date is not a text box. Routed to the calendar rather than the native input for the
  // reasons on DatePicker — this wrapper is the only place the wizard's date fields are
  // rendered, so switching here switches all of them.
  if (type === 'date') {
    return <DatePicker id={id} value={value} onChange={onChange} placeholder={placeholder} />
  }
  if (type === 'datetime-local') {
    return <DateTimePicker id={id} value={value} onChange={onChange} />
  }
  return (
    <input
      id={id}
      type={type}
      list={list}
      maxLength={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      className="erp-input h-9 w-full"
    />
  )
}

/**
 * A whole-number count.
 *
 * `min={0}` is a courtesy, not the rule — the server refuses a negative and its sentence
 * is what is shown. An empty box means "not known", which is a different fact from zero
 * and is why this returns null rather than 0 when cleared.
 */
export function CountInput({
  value, onChange, onBlur, id,
}: {
  value: number | null
  onChange: (v: number | null) => void
  onBlur?: () => void
  id?: string
}) {
  return (
    <input
      id={id}
      type="number"
      min={0}
      inputMode="numeric"
      value={value ?? ''}
      onChange={(e) => {
        const raw = e.target.value
        onChange(raw === '' ? null : Number(raw))
      }}
      onBlur={onBlur}
      placeholder="not known"
      className="erp-input h-9 w-full tabular-nums"
    />
  )
}

/**
 * A `<select>` driven by a SERVED code array.
 *
 * `codes` comes from `/incidents/vocabulary/*`. This component cannot add an option, and
 * that is the point: a hand-written option list that drifts from the server produces a
 * dropdown whose every choice 400s, and it does it silently.
 */
export function CodeSelect({
  codes, value, onChange, id, allowEmpty = true, emptyLabel = '—',
}: {
  codes: string[] | null | undefined
  value: string | null
  onChange: (v: string | null) => void
  id?: string
  allowEmpty?: boolean
  emptyLabel?: string
}) {
  const opts = toOptions(codes)
  return (
    <select
      id={id}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      className="erp-input h-9 w-full"
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      {/*
         A value the server sent that is not in the served list — an older row, or a code
         retired since. Rendering it keeps the field honest instead of silently snapping
         the row to the first option on the next save.
      */}
      {value && !opts.some((o) => o.value === value) && (
        <option value={value}>{value} (no longer offered)</option>
      )}
    </select>
  )
}

export function TextArea({
  value, onChange, onBlur, id, rows = 5, max,
}: {
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  id?: string
  rows?: number
  max?: number
}) {
  return (
    <div>
      <textarea
        id={id}
        rows={rows}
        maxLength={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="erp-input w-full py-2 resize-y"
      />
      {max && (
        <div className={cn(
          'mt-1 text-right text-erp-xs tabular-nums',
          value.length > max * 0.9 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground/70',
        )}>
          {value.length.toLocaleString()} / {max.toLocaleString()}
        </div>
      )}
    </div>
  )
}

/** An ISO instant ⇄ the value a `datetime-local` input wants, in the browser's zone. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
